import React from "react";
import {
  getFirestore,
  collection,
  getDocs,
  addDoc,
  doc,
  getDoc,
  query,
  where,
  updateDoc,
} from "firebase/firestore";
import DashboardSidebar from "../component/dash/DashboardSidebar";
import themeLarge from "../component/ThemeLarge";
import { showNotification } from "../uti";

class BlogSeriesAdminPanel extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isSidebarOpen: false,
      seriesList: [],
      showCreateModal: false,
      newSeriesTitle: "",
      newSeriesDescription: "",
      blogSearchQuery: "",
      blogSearchResults: [],
      selectedBlogs: [],
      isLoading: false,
    };
  }

  componentDidMount() {
    this.fetchAllSeries();
  }

  toggleSidebar = () => {
    this.setState((prev) => ({ isSidebarOpen: !prev.isSidebarOpen }));
  };

  fetchAllSeries = async () => {
    const db = getFirestore();
    const snapshot = await getDocs(collection(db, "blogSeries"));
    const seriesList = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    this.setState({ seriesList });
  };

  handleCreateSeries = async () => {
    const { newSeriesTitle, newSeriesDescription, selectedBlogs } = this.state;
    if (!newSeriesTitle.trim()) {
      showNotification("Series title is required!", "error", 2000);
      return;
    }
    if (selectedBlogs.length === 0) {
      showNotification("Add at least one blog to the series!", "error", 2000);
      return;
    }

    this.setState({ isLoading: true });

    try {
      const db = getFirestore();
      const newSeries = {
        title: newSeriesTitle.trim(),
        description: newSeriesDescription.trim(),
        blogs: selectedBlogs.map((b, i) => ({ blogId: b.id, position: i + 1 })),
        createdAt: new Date().toISOString(),
      };

      const docRef = await addDoc(collection(db, "blogSeries"), newSeries);

      for (const b of selectedBlogs) {
        const blogRef = doc(db, "blogs", b.id);
        await updateDoc(blogRef, { seriesId: docRef.id });
      }

      showNotification("Series created successfully!", "success", 3000);

      this.setState({
        showCreateModal: false,
        newSeriesTitle: "",
        newSeriesDescription: "",
        blogSearchQuery: "",
        blogSearchResults: [],
        selectedBlogs: [],
        isLoading: false,
      });

      this.fetchAllSeries();
    } catch (err) {
      console.error("Error creating series:", err);
      showNotification("Error creating series!", "error", 3000);
      this.setState({ isLoading: false });
    }
  };

  searchBlogs = async () => {
    const { blogSearchQuery } = this.state;
    if (!blogSearchQuery.trim()) return;

    const db = getFirestore();
    const q = query(
      collection(db, "blogs"),
      where("status", "==", "published")
    );

    const snapshot = await getDocs(q);

    const results = snapshot.docs
      .map((doc) => ({ id: doc.id, ...doc.data() }))
      .filter(
        (blog) =>
          blog.title &&
          blog.title.toLowerCase().includes(blogSearchQuery.toLowerCase())
      );

    this.setState({ blogSearchResults: results.length > 0 ? results : [] });
  };

  handleAddBlog = (blog) => {
    if (blog.seriesId) {
      showNotification("This blog is already part of a series!", "error", 2000);
      return;
    }
    if (this.state.selectedBlogs.some((b) => b.id === blog.id)) {
      showNotification("This blog is already added!", "error", 2000);
      return;
    }
    this.setState((prev) => ({
      selectedBlogs: [...prev.selectedBlogs, blog],
    }));
  };

  handleRemoveBlog = (index) => {
    const updated = [...this.state.selectedBlogs];
    updated.splice(index, 1);
    this.setState({ selectedBlogs: updated });
  };

  render() {
    const {
      isSidebarOpen,
      seriesList,
      showCreateModal,
      newSeriesTitle,
      newSeriesDescription,
      blogSearchQuery,
      blogSearchResults,
      selectedBlogs,
      isLoading,
    } = this.state;

    return (
      <div
        className="container-fluid"
        style={{
          marginTop: "85px",
          fontFamily: "Kumbh Sans",
          color: themeLarge.colors.text,
          fontSize: themeLarge.fontSize.body,
        }}
      >
        <div className="row">
          {/* Sidebar - fixed width */}
          <div className="col-auto">
            <DashboardSidebar
              isOpen={isSidebarOpen}
              onToggle={this.toggleSidebar}
            />
          </div>

          {/* Main Content */}
          <div className="col-md-10" style={{ padding: "20px" }}>
            <div className="row align-items-center justify-content-between mb-3">
              <div className="col-auto">
                <h1
                  style={{
                    fontFamily: "Kumbh Sans",
                    color: themeLarge.colors.primary,
                    margin: 0,
                    fontSize: themeLarge.fontSize.display,
                  }}
                >
                  Blog Series
                </h1>
              </div>

              <div className="col-auto">
                <button
                  className="btn"
                  style={{
                    background: themeLarge.colors.primary,
                    color: themeLarge.colors.bg,
                    fontFamily: "Kumbh Sans",
                    fontSize: themeLarge.fontSize.body,
                    padding: "10px",
                    borderRadius: "5px",
                  }}
                  onClick={() => this.setState({ showCreateModal: true })}
                >
                  Create New Series
                </button>
              </div>
            </div>

            <hr />

            {seriesList.length === 0 ? (
              <p
                style={{
                  fontSize: themeLarge.fontSize.h4,
                }}
              >
                <span
                  style={{
                    fontSize: themeLarge.fontSize.h1,
                    color: themeLarge.colors.gradient,
                  }}
                >
                  No series yet...
                </span>
                {""} <br />
                Your stories are waiting to unite and become a saga!
              </p>
            ) : (
              <ul>
                {seriesList.map((s) => (
                  <li key={s.id}>
                    <strong>{s.title}</strong> — {s.blogs?.length || 0} blogs
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {showCreateModal && (
          <div className="modal show d-block" tabIndex="-1" role="dialog">
            <div className="modal-dialog modal-lg" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Create New Series</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => this.setState({ showCreateModal: false })}
                  ></button>
                </div>
                <div className="modal-body">
                  <label>Series Title</label>
                  <input
                    className="form-control"
                    value={newSeriesTitle}
                    onChange={(e) =>
                      this.setState({ newSeriesTitle: e.target.value })
                    }
                  />

                  <label className="mt-2">Series Description</label>
                  <textarea
                    className="form-control"
                    value={newSeriesDescription}
                    onChange={(e) =>
                      this.setState({ newSeriesDescription: e.target.value })
                    }
                  />

                  <label className="mt-3">Add Blogs</label>
                  <div className="d-flex">
                    <input
                      className="form-control"
                      placeholder="Search blogs by title..."
                      value={blogSearchQuery}
                      onChange={(e) =>
                        this.setState({ blogSearchQuery: e.target.value })
                      }
                    />
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={this.searchBlogs}
                      style={{
                        marginLeft: "10px",
                        background: themeLarge.colors.primary,
                        color: themeLarge.colors.bg,
                      }}
                    >
                      Search
                    </button>
                  </div>

                  <div className="mt-2">
                    {blogSearchResults.length > 0 ? (
                      <ul>
                        {blogSearchResults.map((blog) => (
                          <li key={blog.id}>
                            {blog.title}{" "}
                            <button
                              className="btn btn-sm btn-outline-success"
                              onClick={() => this.handleAddBlog(blog)}
                              style={{
                                marginLeft: "10px"
                              }}
                            >
                              Add
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : blogSearchQuery.trim() ? (
                      <p>No matching published blogs found.</p>
                    ) : null}
                  </div>

                  {selectedBlogs.length > 0 && (
                    <div className="mt-3">
                      <strong>Selected Blogs:</strong>
                      <ul>
                        {selectedBlogs.map((b, index) => (
                          <li key={b.id}>
                            {b.title}{" "}
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => this.handleRemoveBlog(index)}
                              style={{
                                marginLeft: "10px"
                              }}
                            >
                              Remove
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => this.setState({ showCreateModal: false })}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn"
                    onClick={this.handleCreateSeries}
                    disabled={isLoading}
                    style={{
                      background: themeLarge.colors.primary,
                      color: themeLarge.colors.bg,
                    }}
                  >
                    {isLoading ? "Creating..." : "Create Series"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default BlogSeriesAdminPanel;
